// src/components/Quiz.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getDocs, collection, query, where, addDoc, updateDoc, arrayUnion, doc, getDoc, orderBy, limit } from 'firebase/firestore';
import { db } from '../firebase';
import { Button, ProgressBar, Card } from 'react-bootstrap';
import '../css/Quiz.css';
import MasterLayout from '../layouts/MasterLayout';
import Leaderboard from './Leaderboard';
import { CircularProgressbar } from 'react-circular-progressbar';

const Quiz = ({ playerData }) => {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [score, setScore] = useState(0);
  const [points, setPoints] = useState(0);
  const [timeLeft, setTimeLeft] = useState(30);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [quizOver, setQuizOver] = useState(false);
  const [quizPlayedToday, setQuizPlayedToday] = useState(false);
  const [showLeaderboard, setShowLeaderboard] = useState(false); // State to control leaderboard visibility
  const [animate, setAnimate] = useState(false); // State to control animation
  const [isSpeaking, setIsSpeaking] = useState(true);
  const [pendingSpeakToggle, setPendingSpeakToggle] = useState(false);

  useEffect(() => {
    const checkQuizPlayedToday = async () => {
      const playerRef = doc(db, 'players', playerData.id);
      const docSnap = await getDoc(playerRef);
      const today = new Date().toISOString().split('T')[0];
      const playedToday = docSnap.data().quizPlayedToday || [];
      setQuizPlayedToday(playedToday.includes(today));
    };

    if (playerData.id) {
      checkQuizPlayedToday();
    }
  }, [playerData.id]);

  useEffect(() => {
    const fetchQuestions = async (date) => {
      const q = query(collection(db, 'quizzes'), where('publicationDate', '==', date));
      const querySnapshot = await getDocs(q);
      if (!querySnapshot.empty) {
        const quizData = querySnapshot.docs[0].data().questions;
        const shuffledQuestions = shuffleArray(quizData).slice(0, 5); // Shuffle and pick 5 unique questions
        setQuestions(shuffledQuestions);
      }
      return !querySnapshot.empty;
    };

    const getQuizForTodayOrYesterday = async () => {
      const today = new Date().toISOString().split('T')[0];
      const yesterday = new Date(Date.now() - 86400000).toISOString().split('T')[0];

      let quizFetched = await fetchQuestions(today);
      if (!quizFetched) {
        quizFetched = await fetchQuestions(yesterday);
      }

      if (!quizFetched) {
        console.log('No quiz available for today or yesterday.');
      }
    };

    if (!quizPlayedToday) {
      getQuizForTodayOrYesterday();
    }
  }, [quizPlayedToday]);

  const shuffleArray = (array) => {
    const shuffledArray = [...array];
    for (let i = shuffledArray.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffledArray[i], shuffledArray[j]] = [shuffledArray[j], shuffledArray[i]];
    }
    return shuffledArray;
  };

  useEffect(() => {
    if (timeLeft > 0 && !quizOver) {
      const timer = setTimeout(() => {
        setTimeLeft(timeLeft - 1);// timer 
      }, 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0) {
      handleNextQuestion();
    }
  }, [timeLeft, quizOver]);

  useEffect(() => {
    if (questions[currentQuestion]) {
      if (isSpeaking) {
        speakQuestion(questions[currentQuestion].question);
      } else {
        window.speechSynthesis.cancel();
      }
    }
  }, [isSpeaking, questions, currentQuestion]);

  useEffect(() => {
    if (pendingSpeakToggle) {
      setIsSpeaking((prev) => !prev);
      setPendingSpeakToggle(false);
    }
  }, [pendingSpeakToggle]);

  const speakQuestion = (questionText) => {
    const synth = window.speechSynthesis;
    synth.cancel(); // Stop any ongoing speech
    if (isSpeaking) {
      const utterance = new SpeechSynthesisUtterance(questionText);
      utterance.lang = 'en-IN';
      synth.speak(utterance);
    }
  };
  const handleAnswerSelect = (answer) => {
    setSelectedAnswer(answer);
    let correct = false;
    if (answer === questions[currentQuestion].correct) {
      setScore((prevScore) => prevScore + 1);
      correct = true;
      let bonusPoints = 0;
      if (timeLeft > 28) {
        bonusPoints = 10;
      } else if (timeLeft > 26) {
        bonusPoints = 9;
      } else if (timeLeft > 23) {
        bonusPoints = 8;
      } else if (timeLeft > 19) {
        bonusPoints = 7;
      } else if (timeLeft > 15) {
        bonusPoints = 6;
      } else if (timeLeft > 12) {
        bonusPoints = 5;
      } else if (timeLeft > 8) {
        bonusPoints = 4;
      } else if (timeLeft > 4) {
        bonusPoints = 2;
      }      
      setPoints((prevPoints) => prevPoints + 10 + bonusPoints);
    }
    setTimeout(() => handleNextQuestion(correct), 2000);
  };

  const handleShowLeaderboard = () => {
    setShowLeaderboard(true);
    setTimeout(() => setAnimate(true), 0); // Trigger the animation
  };

  const handleCloseLeaderboard = () => {
    setAnimate(false);
    setTimeout(() => setShowLeaderboard(false), 500); // Wait for animation to complete
  };
  const handleNextQuestion = (correct = false) => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
      setSelectedAnswer(null);
      setTimeLeft(30);
    } else {
      setQuizOver(true);
      const finalScore = score + (correct ? 1 : 0);
      const bonusPoints = correct ? (timeLeft > 20 ? 10 : timeLeft > 10 ? 5 : 0) : 0;
      const finalPoints = points + (correct ? 10 + bonusPoints : 0);
      savePerformance(finalScore, finalPoints);
      navigate('/result', { state: { score: finalScore, total: questions.length, points: finalPoints } });
    }
  };

  const savePerformance = async (finalScore, finalPoints) => {
    if (quizPlayedToday) {
      return; // Do not save performance if quiz is already played today
    }
  
    const performance = {
      playerId: playerData.id,
      playerName: playerData.name,
      date: new Date().toISOString(),
      score: finalScore,
      points: finalPoints,
      correctCount: finalScore,
      wrongCount: questions.length - finalScore,
    };
  
    try {
      await addDoc(collection(db, 'performances'), performance);
  
      // Update the player's document to mark today's quiz as played and update total points
      const today = new Date().toISOString().split('T')[0];
      const playerRef = doc(db, 'players', playerData.id);
  
      // Fetch the current total points
      const playerSnap = await getDoc(playerRef);
      const currentTotalPoints = playerSnap.data().totalPoints || 0;
  
      // Calculate new total points
      const newTotalPoints = currentTotalPoints + finalPoints;
  
      await updateDoc(playerRef, {
        quizPlayedToday: arrayUnion(today),
        totalPoints: newTotalPoints, // Update total points
      });
    } catch (error) {
      console.error('Error saving performance: ', error);
    }
  };

  if (quizPlayedToday) {
    return (
      <MasterLayout>
        <Card className="quiz-card">
          <Card.Body className="text-center">

            <h4>You have already played the quiz today.</h4>
            <p>Come back tomorrow for a new quiz. In the meantime, you can check out the leaderboard to see how you compare with others or go back to the home page.</p>
            <div className="d-flex justify-content-center mt-4">
              <Button variant="primary" className="me-2" onClick={() => navigate('/result')}>
                Result
              </Button>
              {/* <Button variant="secondary" className="mt-3" onClick={handleShowLeaderboard}>
              Leaderboard
            </Button> */}
            </div>
          </Card.Body>
          {showLeaderboard && (
          <div className="leaderboard-overlay">
            <Leaderboard onClose={handleCloseLeaderboard} animate={animate} />
          </div>
        )}
        </Card>
      </MasterLayout>
    );
  }

  if (questions.length === 0) {
    return <div>Loading...</div>;
  }

  const question = questions[currentQuestion];
  const progress = (timeLeft / 30) * 100;
  
  const progressPercentage = ((currentQuestion + 1) / questions.length) * 100;

  return (
    <MasterLayout>
      <Card className="quiz-card">
        <div className='progress-box'>
        <button
          onClick={() => setIsSpeaking(prev => !prev)}
          className={`speech-toggle-button ${isSpeaking ? 'speaking' : 'not-speaking'}`}
        ></button>
      <div className="progress-bar">

      <div
        className="progress-bar-fill"
        style={{ width: `${progressPercentage}%`}}
      ></div>
      </div>
      <span className="progress-bar-text">{currentQuestion + 1 }/{questions.length}</span>
    </div>
        <Card.Body>

        <div className='Question-box'>
        <CircularProgressbar
            value={progress}
            text={`${timeLeft}s`}
            circleRatio={0.99}
            styles={{
              root: {

              },
              path: {
                stroke: `rgba(62, 152, 199, ${progress / 100})`,
                strokeLinecap: 'round',
                transition: 'stroke-dashoffset 0.5s ease 0s',
              },
              trail: {
                stroke: '#d6d6d6',
                strokeLinecap: 'round',
              },
              text: {
                fill: '#f77',
                fontSize: '36px',
                textAlign: 'center', // add this to center the text horizontally
                verticalAlign: 'middle', 
                transform: 'translate(2px, 5px)',
                fontWeight: '600',
              },
            }}
          />

            <h4 className="question-text mb-4">{question.question}</h4>
                {/* Display the image if available */}
    {question.questionImage && (
      <img
        src={question.questionImage}
        alt="Question visual"
        style={{ width: '100%', height: '250px', objectFit: 'cover', marginBottom: '15px' }}
      />
    )}
          </div>

          <div className="options">
            {question.options.map((option, index) => (
              <Button
                key={index}
                className={`w-100 mb-3 ${selectedAnswer ? (option === question.correct ? 'correct' : (option === selectedAnswer ? 'incorrect' : '')) : ''}`}
                onClick={() => handleAnswerSelect(option)}
                disabled={!!selectedAnswer}
              >
                {option}
              </Button>
            ))}
          </div>
          <div className='sc-pt'>
            <div className="score-text mt-3">Score: {score}</div>
            <div className="points-text mt-3">Points: {points}</div>
          </div>
          
        </Card.Body>
      </Card>
    </MasterLayout>
  );
};

export default Quiz;
